// api.js
import axios from 'axios';

// Base API URL - match the mobile configuration for consistency
const isDev = process.env.NODE_ENV !== 'production';
// Use the same API URL pattern as mobile
const API_URL = isDev
  ? 'http://localhost:5001/api'
  : 'https://api.memoraize.io/api';

console.log(`API configured for ${isDev ? 'development' : 'production'}: ${API_URL}`);

// Create axios instance for API calls
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000 // 10 second timeout
});

// Request interceptor for adding auth token
axiosInstance.interceptors.request.use(
  async (config) => {
    // Get auth token from local storage
    const token = localStorage.getItem('token');

    // If token exists, add to headers
    if (token) {
      config.headers['x-auth-token'] = token;
      console.log('Token attached to request');
    }

    // Add center filtering for GET requests, but skip for super_admin
    if (config.method?.toLowerCase() === 'get') {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        // Only add center parameter for non-super_admin users
        if (user && user.center && user.role !== 'super_admin') {
          // Add center parameter to query params
          config.params = config.params || {};
          
          // Specifically for users endpoint, we don't need to add center param explicitly
          // The backend will enforce center restrictions automatically
          if (!config.url.includes('/users')) {
            if (!config.params.center) {
              config.params.center = user.center;
              console.log('Center filter added:', user.center);
            }
          } else {
            console.log('Users endpoint - center filtering handled by backend');
          }
        } else if (user.role === 'super_admin') {
          console.log('Super admin request - skipping center filter');
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
axiosInstance.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    if (error.response) {
      // The request was made and the server responded with error
      console.log('Response Error:', error.response.status, error.response.data);

      // Handle 401 error (unauthorized)
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('center');
        console.log('Unauthorized - session expired, redirecting to login');
        
        // Redirect to login page
        window.location.href = '/login';
      }
    } else if (error.request) {
      // Request was made but no response
      console.log('Network Error - No response received');
    } else {
      // Something else caused error
      console.log('Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// API wrapper for simplified usage
const api = {
  // Auth endpoints
  login: async (email, password) => {
    return axiosInstance.post('/auth/login', { email, password });
  },

  register: async (userData) => {
    return axiosInstance.post('/auth/register', userData);
  },

  forgotPassword: async (email) => {
    return axiosInstance.post('/auth/forgot-password', { email });
  },

  // Users endpoints
  getCurrentUser: async () => {
    return axiosInstance.get('/auth/me');
  },

  // Centers endpoints
  getCenter: async (centerId) => {
    return axiosInstance.get(`/centers/${centerId}`);
  },
  
  getCenterBySubdomain: async (subdomain) => {
    return axiosInstance.get(`/centers/subdomain/${subdomain}`);
  },
  
  updateCenterBranding: async (centerId, brandingData) => {
    return axiosInstance.put(`/centers/${centerId}/branding`, brandingData);
  },

  // Generic request methods
  get: async (endpoint, config = {}) => {
    return axiosInstance.get(endpoint, config);
  },

  post: async (endpoint, data = {}, config = {}) => {
    return axiosInstance.post(endpoint, data, config);
  },

  put: async (endpoint, data = {}, config = {}) => {
    return axiosInstance.put(endpoint, data, config);
  },

  delete: async (endpoint, config = {}) => {
    return axiosInstance.delete(endpoint, config);
  }
};

export default api;